import React from "react";
import { Button, Form, Row, Col, Table, Card, Spinner } from "react-bootstrap";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { guarantorFieldConfig, collateralTypeConfig, getCollateralDescription, getCollateralEstimatedValue } from "../formConfig";
import CommonMobileInput from "../../../components/Common/CommonMobileInput";
import CommonRichTextEditor from "../../../components/Common/CommonRichTextEditor";
import { DATE_FORMAT } from "../../../helpers/dateFormat";

const splitFieldConfig = (fields) => ({
  regularFields: fields.filter((field) => field.type !== "richtext"),
  richTextFields: fields.filter((field) => field.type === "richtext"),
});

const GuarantorCollateralStep = ({
  disabled,
  // Guarantor props
  guarantors,
  showGuarantorModal,
  editingGuarantorId,
  guarantorForm,
  guarantorPhotoUploading,
  guarantorPhotoPreview,
  guarantorSignatureUploading,
  guarantorSignaturePreview,
  handleGuarantorFormChange,
  handleAddGuarantor,
  handleEditGuarantor,
  handleRemoveGuarantor,
  handleGuarantorPhotoUpload,
  handleGuarantorSignatureUpload,
  closeGuarantorModal,
  openAddGuarantorModal,
  // Collateral props
  collaterals,
  collateralTypes,
  showCollateralModal,
  editingCollateralId,
  collateralForm,
  collateralPhotoUploading,
  collateralPhotoPreview,
  handleCollateralFormChange,
  handleCollateralTypeChange,
  handleAddCollateral,
  handleEditCollateral,
  handleRemoveCollateral,
  handleCollateralPhotoUpload,
  closeCollateralModal,
  openAddCollateralModal,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <h5 className="mb-3">{t("customer.add.steps.step2")}</h5>

      {/* ===== GUARANTOR SECTION ===== */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">{t("customer.add.step2.guarantorSectionTitle")}</h6>
          {!disabled && (
            <Button variant="primary" size="sm" onClick={openAddGuarantorModal}>
              <i className="ri-add-line me-1"></i>
              {t("customer.add.step2.addGuarantor")}
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {guarantors.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("customer.add.step2.guarantorTable.name")}</th>
                  <th>{t("customer.add.step2.guarantorTable.mobile")}</th>
                  <th>{t("customer.add.step2.guarantorTable.idProof")}</th>
                  <th>{t("customer.add.step2.guarantorTable.relationship")}</th>
                  <th>{t("customer.add.step2.guarantorTable.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {guarantors.map((g, idx) => (
                  <tr key={g.id}>
                    <td>{idx + 1}</td>
                    <td>{g.fullName}</td>
                    <td>{g.mobileNumber}</td>
                    <td>
                      {g.idProofType
                        ? `${t(`customer.add.step2.idProofTypes.${g.idProofType}`)} - ${g.idNumber || ""}`
                        : "-"}
                    </td>
                    <td>{g.relationshipWithBorrower || "-"}</td>
                    <td>
                      <Button variant="warning" size="sm" className="me-1" onClick={() => handleEditGuarantor(g)}>
                        {!disabled ? <i className="ri-eye-fill"></i> : <i className="ri-eye-fill"></i>}
                      </Button>
                      {!disabled && (
                        <Button variant="danger" size="sm" onClick={() => handleRemoveGuarantor(g.id)}>
                          <i className="ri-delete-bin-fill"></i>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted text-center mb-0">{t("customer.add.step2.noGuarantors")}</p>
          )}
        </Card.Body>
      </Card>

      {/* ===== GUARANTOR MODAL ===== */}
      <GuarantorModal
        show={showGuarantorModal}
        onClose={closeGuarantorModal}
        editingId={editingGuarantorId}
        form={guarantorForm}
        onFormChange={handleGuarantorFormChange}
        onSave={handleAddGuarantor}
        disabled={disabled}
        photoUploading={guarantorPhotoUploading}
        photoPreview={guarantorPhotoPreview}
        signatureUploading={guarantorSignatureUploading}
        signaturePreview={guarantorSignaturePreview}
        onPhotoUpload={handleGuarantorPhotoUpload}
        onSignatureUpload={handleGuarantorSignatureUpload}
      />

      {/* ===== COLLATERAL SECTION ===== */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">{t("customer.add.step2.collateralSectionTitle")}</h6>
          {!disabled && (
            <Button variant="primary" size="sm" onClick={openAddCollateralModal}>
              <i className="ri-add-line me-1"></i>
              {t("customer.add.step2.addCollateral")}
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {collaterals.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("customer.add.step2.table.type")}</th>
                  <th>{t("customer.add.step2.table.description")}</th>
                  <th>{t("customer.add.step2.table.estimatedValue")}</th>
                  <th>{t("customer.add.step2.table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {collaterals.map((c, idx) => (
                  <tr key={c.id}>
                    <td>{idx + 1}</td>
                    <td>{collateralTypes.find((ct) => ct.value === c.type)?.label || c.type}</td>
                    <td>{getCollateralDescription(c)}</td>
                    <td>₹{getCollateralEstimatedValue(c)}</td>
                    <td>
                      <Button variant="warning" size="sm" className="me-1" onClick={() => handleEditCollateral(c)}>
                        {!disabled ? <i className="ri-eye-fill"></i> : <i className="ri-eye-fill"></i>}
                      </Button>
                      {!disabled && (
                        <Button variant="danger" size="sm" onClick={() => handleRemoveCollateral(c.id)}>
                          <i className="ri-delete-bin-fill"></i>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted text-center mb-0">{t("customer.add.step2.noCollaterals")}</p>
          )}
        </Card.Body>
      </Card>

      {/* ===== COLLATERAL MODAL ===== */}
      <CollateralModal
        show={showCollateralModal}
        onClose={closeCollateralModal}
        editingId={editingCollateralId}
        form={collateralForm}
        onFormChange={handleCollateralFormChange}
        onTypeChange={handleCollateralTypeChange}
        onSave={handleAddCollateral}
        disabled={disabled}
        collateralTypes={collateralTypes}
        photoUploading={collateralPhotoUploading}
        photoPreview={collateralPhotoPreview}
        onPhotoUpload={handleCollateralPhotoUpload}
      />
    </div>
  );
};

// ===== GUARANTOR MODAL COMPONENT =====
const GuarantorModal = ({
  show,
  onClose,
  editingId,
  form,
  onFormChange,
  onSave,
  disabled,
  photoUploading,
  photoPreview,
  signatureUploading,
  signaturePreview,
  onPhotoUpload,
  onSignatureUpload,
}) => {
  const { t } = useTranslation();
  const { regularFields, richTextFields } = splitFieldConfig(guarantorFieldConfig);

  const renderGuarantorFieldInput = (field) => {
    if (field.type === "select") {
      return (
        <Form.Select value={form[field.name] || ""} onChange={(e) => onFormChange(field.name, e.target.value)}>
          <option value="">Select</option>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
          ))}
        </Form.Select>
      );
    }
    if (field.type === "file") {
      return (
                  <div>
                    <div className="d-flex align-items-center gap-3 mb-2">
                      <div className="flex-grow-1">
                        <Form.Control
                          type="file"
                          accept="image/*"
                          disabled={field.name === "photo" ? photoUploading : signatureUploading}
                          onChange={(e) => {
                            const file = e.currentTarget.files[0];
                            if (file) {
                              if (field.name === "photo") onPhotoUpload(file);
                              else if (field.name === "signature") onSignatureUpload(file);
                            }
                          }}
                        />
                      </div>
                      {((field.name === "photo" && photoUploading) ||
                        (field.name === "signature" && signatureUploading)) && (
                        <div className="d-flex align-items-center gap-2">
                          <Spinner animation="border" size="sm" variant="primary" />
                          <span className="text-muted small">Uploading...</span>
                        </div>
                      )}
                    </div>
                    {field.name === "photo" && (photoPreview || form.photo) && !photoUploading && (
                      <div className="mt-2">
                        <img
                          src={photoPreview || form.photo}
                          alt="Preview"
                          style={{ maxWidth: "120px", maxHeight: "120px", objectFit: "cover", borderRadius: "8px", border: "1px solid #dee2e6" }}
                        />
                      </div>
                    )}
                    {field.name === "signature" && (signaturePreview || form.signature) && !signatureUploading && (
                      <div className="mt-2">
                        <img
                          src={signaturePreview || form.signature}
                          alt="Signature"
                          style={{ maxWidth: "120px", maxHeight: "120px", objectFit: "cover", borderRadius: "8px", border: "1px solid #dee2e6" }}
                        />
                      </div>
                    )}
                  </div>
      );
    }
    if (field.type === "tel") {
      return (
        <CommonMobileInput
          name={field.name}
          id={`guarantor-${field.name}`}
          value={form[field.name] || ""}
          onChange={(val) => onFormChange(field.name, val)}
        />
      );
    }
    if (field.type === "textarea") {
      return (
        <Form.Control
          as="textarea"
          rows={field.rows || 2}
          value={form[field.name] || ""}
          onChange={(e) => onFormChange(field.name, e.target.value)}
        />
      );
    }
    return (
      <Form.Control
        type={field.type}
        value={form[field.name] || ""}
        onChange={(e) => onFormChange(field.name, e.target.value)}
        maxLength={field.maxLength}
        step={field.step}
        min={field.min}
      />
    );
  };

  return (
    <Modal isOpen={show} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {editingId ? t("customer.add.step2.guarantorModal.editTitle") : t("customer.add.step2.guarantorModal.title")}
      </ModalHeader>
      <ModalBody>
        <Row>
          {regularFields.map((field) => (
            <Col md={field.col || 6} key={field.name} className="mb-3">
              <Form.Group>
                <Form.Label className={field.required ? "required" : ""}>{t(field.labelKey)}</Form.Label>
                {renderGuarantorFieldInput(field)}
              </Form.Group>
            </Col>
          ))}
        </Row>
        {richTextFields.length > 0 && (
          <Row>
            {richTextFields.map((field) => (
              <Col md={12} key={field.name} className="mb-3">
                <Form.Group>
                  <Form.Label className={field.required ? "required" : ""}>{t(field.labelKey)}</Form.Label>
                  <CommonRichTextEditor
                    editorKey={`guarantor-${field.name}-${editingId || "new"}`}
                    value={form[field.name] || ""}
                    onChange={(data) => onFormChange(field.name, data)}
                    disabled={disabled}
                    minHeight={field.rows ? field.rows * 60 : 180}
                  />
                </Form.Group>
              </Col>
            ))}
          </Row>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>{t("customer.add.buttons.cancel")}</Button>
        {!disabled && (
          <Button variant="primary" onClick={onSave}>
            {editingId ? t("customer.add.step2.guarantorModal.editTitle") : t("customer.add.step2.save")}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

// ===== COLLATERAL MODAL COMPONENT =====
const CollateralModal = ({
  show,
  onClose,
  editingId,
  form,
  onFormChange,
  onTypeChange,
  onSave,
  disabled,
  collateralTypes,
  photoUploading,
  photoPreview,
  onPhotoUpload,
}) => {
  const { t } = useTranslation();

  const config = form.type ? collateralTypeConfig[form.type] : null;
  const { regularFields, richTextFields } = splitFieldConfig(config?.fields || []);

  const renderCollateralFieldInput = (field) => {
    if (field.type === "select") {
      return (
        <Form.Select value={form[field.name] || ""} onChange={(e) => onFormChange(field.name, e.target.value)}>
          <option value="">Select</option>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
          ))}
        </Form.Select>
      );
    }
    if (field.type === "file") {
      return (
        <div>
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="flex-grow-1">
              <Form.Control
                type="file"
                accept="image/*"
                disabled={photoUploading}
                onChange={(e) => {
                  const file = e.currentTarget.files[0];
                  if (file) onPhotoUpload(file);
                }}
              />
            </div>
            {photoUploading && (
              <div className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" variant="primary" />
                <span className="text-muted small">Uploading...</span>
              </div>
            )}
          </div>
          {(photoPreview || form.photo) && !photoUploading && (
            <div className="mt-2">
              <img
                src={photoPreview || form.photo}
                alt="Preview"
                style={{ maxWidth: "120px", maxHeight: "120px", objectFit: "cover", borderRadius: "8px", border: "1px solid #dee2e6" }}
              />
            </div>
          )}
        </div>
      );
    }
    if (field.type === "date") {
      return (
        <DatePicker
          selected={form[field.name] ? new Date(form[field.name]) : null}
          onChange={(date) => onFormChange(field.name, date)}
          dateFormat={DATE_FORMAT.DISPLAY}
          className="form-control w-100"
          wrapperClassName="w-100"
        />
      );
    }
    return (
      <Form.Control
        type={field.type}
        value={form[field.name] || ""}
        onChange={(e) => onFormChange(field.name, e.target.value)}
        step={field.step}
        min={field.min}
      />
    );
  };

  return (
    <Modal isOpen={show} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {editingId ? t("customer.add.step2.collateralModal.editTitle") : t("customer.add.step2.collateralModal.title")}
      </ModalHeader>
      <ModalBody>
        {/* Collateral Type Selection */}
        <Row className="mb-3">
          <Col md={12}>
            <Form.Group>
              <Form.Label className="required">{t("customer.add.step2.collateral.type")}</Form.Label>
              <Form.Select
                value={form.type}
                onChange={(e) => onTypeChange(e.target.value)}
                disabled={!!editingId}
              >
                <option value="">{t("customer.add.step2.collateral.selectType")}</option>
                {collateralTypes.map((ct) => (
                  <option key={ct.value} value={ct.value}>{ct.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Dynamic Fields based on selected collateral type */}
        {form.type && config && (
          <>
            <Row>
              {regularFields.map((field) => (
                <Col md={6} key={field.name} className="mb-3">
                  <Form.Group>
                    <Form.Label className={field.required ? "required" : ""}>{t(field.labelKey)}</Form.Label>
                    {renderCollateralFieldInput(field)}
                  </Form.Group>
                </Col>
              ))}
            </Row>
            {richTextFields.length > 0 && (
              <Row>
                {richTextFields.map((field) => (
                  <Col md={12} key={field.name} className="mb-3">
                    <Form.Group>
                      <Form.Label className={field.required ? "required" : ""}>{t(field.labelKey)}</Form.Label>
                      <CommonRichTextEditor
                        editorKey={`collateral-${field.name}-${form.type}-${editingId || "new"}`}
                        value={form[field.name] || ""}
                        onChange={(data) => onFormChange(field.name, data)}
                        disabled={disabled}
                        minHeight={field.rows ? field.rows * 60 : 180}
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>{t("customer.add.buttons.cancel")}</Button>
        {!disabled && (
          <Button variant="primary" onClick={onSave}>
            {editingId ? t("customer.add.step2.collateralModal.editTitle") : t("customer.add.step2.save")}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default GuarantorCollateralStep;
